import { useEffect, useLayoutEffect, useRef, type FocusEvent, type PointerEvent, type RefObject } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import type { SignalState, SignalView } from '@entities/Generation';
import { cn } from '@shared/lib';
import { StatusGlyph, StatusSheen, type StatusGlyphKind } from '@shared/ui';

const RISE: [number, number, number, number] = [0.2, 0.9, 0.2, 1];
const FALL: [number, number, number, number] = [0.4, 0, 1, 1];

const BORDER: Record<SignalState, string> = {
  generating: 'border-accent/30',
  slow: 'border-line',
  ready: 'border-success/30',
  added: 'border-success/30',
  failed: 'border-warn/30',
};

const GLYPH: Record<SignalState, StatusGlyphKind> = {
  generating: 'generating',
  slow: 'generating',
  ready: 'ready',
  added: 'ready',
  failed: 'failed',
};

function ArrowDownIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M12 5v14M6 13l6 6 6-6" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

/** Ширина сигнала компьютера доезжает за 160 мс при смене текста (ТЗ § 13): FLIP от прежней ширины. */
function useWidthGlide(ref: RefObject<HTMLElement>, key: string, enabled: boolean) {
  const last = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transition = '';
    el.style.width = '';
    const to = el.offsetWidth;
    const from = last.current;
    last.current = to;
    if (!enabled || from === null || Math.abs(from - to) < 1) return;
    el.style.width = `${from}px`;
    void el.offsetWidth;
    el.style.transition = 'width 160ms cubic-bezier(0.2, 0.9, 0.2, 1)';
    el.style.width = `${to}px`;
    const timer = window.setTimeout(() => {
      el.style.transition = '';
      el.style.width = '';
    }, 200);
    return () => window.clearTimeout(timer);
    // По составу текста.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);
}

export interface ComposerSignalProps {
  view: SignalView | null;
  /** Телефон: во всю ширину поля, скругление 14 px; компьютер — по левому краю, по ширине текста, до 480 px. */
  phone: boolean;
  onShow: () => void;
  onDismiss: () => void;
  /** Курсор или фокус на сигнале — отсчёт «Готово» стоит (ТЗ § 8). */
  onHold: (held: boolean) => void;
  /** Строка сверху страницы — сигнал под ней (2026-10-28). */
  below?: boolean;
}

/**
 * Сигнал у строки создания (ТЗ главной § 8): короткий живой статус со ссылкой к записи «Недавно
 * добавленных» — «Генерируем travel · 2 карточки», «Готово: travel», «Не получилось: travel · 1 из 2» и
 * «+N», когда генераций несколько. Не копия записи: без состава и без строк по карточкам. Стоит
 * абсолютно над карточкой строки и поток не двигает (место под ним — отступ колонки главной, § 5).
 * Подписи по ширине окна прячет CSS (`HomeComposer.css`, «Состав по ширине»). Сам сигнал не live:
 * объявляет скрытая область полосы.
 */
export function ComposerSignal({ view, phone, onShow, onDismiss, onHold, below = false }: ComposerSignalProps) {
  const reduced = useReducedMotion() ?? false;
  return (
    <AnimatePresence initial={false}>
      {view && (
        <motion.div
          key="signal"
          className={cn(
            'absolute flex flex-col justify-end overflow-hidden',
            below
              ? phone
                ? '-inset-x-px top-[calc(100%+9px)] !justify-start'
                : 'left-0 top-[calc(100%+8px)] max-w-[min(100%,480px)] origin-top-left !justify-start'
              : phone
                ? '-inset-x-px bottom-[calc(100%+9px)]'
                : 'bottom-[calc(100%+8px)] left-0 max-w-[min(100%,480px)] origin-bottom-left',
          )}
          // Появление: телефон — высота 0 → 44 и прозрачность; компьютер — масштаб 0,98 → 1 и прозрачность (220 мс).
          // Уход: прозрачность и высота до 0 (200 мс).
          initial={reduced ? false : phone ? { height: 0, opacity: 0 } : { opacity: 0, scale: 0.98 }}
          animate={{ height: 44, opacity: 1, scale: 1, transition: { duration: reduced ? 0 : 0.22, ease: RISE } }}
          exit={{ height: 0, opacity: 0, transition: { duration: reduced ? 0 : 0.2, ease: FALL } }}
        >
          <SignalBox view={view} phone={phone} reduced={reduced} onShow={onShow} onDismiss={onDismiss} onHold={onHold} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function SignalBox({
  view,
  phone,
  reduced,
  onShow,
  onDismiss,
  onHold,
}: ComposerSignalProps & { view: SignalView; reduced: boolean }) {
  const { t } = useTranslation();
  const boxRef = useRef<HTMLDivElement>(null);
  const contentKey = `${view.id}:${view.state}`;
  // Сигнал удаляется из DOM без pointerleave и blur (ушёл из-под курсора при прокрутке, ✕ с фокусом):
  // снимаем «курсор или фокус на сигнале», иначе отсчёт «Готово» у следующего сигнала стоял бы.
  useEffect(() => () => onHold(false), [onHold]);
  useWidthGlide(boxRef, `${contentKey}:${view.more}:${view.cards}:${view.failedOf?.failed}`, !phone && !reduced);

  const topic = view.kind === 'topic';
  const title = topic ? t('generations.topic', { topic: view.title }) : view.title;

  // Подпись и заголовок — одна строка текста через обычный пробел: многоточие достаётся заголовку (§ 8).
  let label;
  switch (view.state) {
    case 'generating':
      label = t('homeComposer.signal.generating');
      break;
    case 'slow':
      // До 390 px «Дольше обычного:» не помещается — там «Генерируем» (медленность видна в записи).
      label = (
        <>
          <span className="hc-sig-wide">{t('homeComposer.signal.slow')}</span>
          <span className="hc-sig-narrow">{t('homeComposer.signal.generating')}</span>
        </>
      );
      break;
    case 'ready':
      label = t('homeComposer.signal.ready');
      break;
    case 'added':
      label = t('homeComposer.signal.added');
      break;
    case 'failed':
      // До 390 px — «Не вышло:»: с «Не получилось:» длинное слово на 320 px уходит в многоточие.
      label = (
        <>
          <span className="hc-sig-wide">{t('homeComposer.signal.failed')}</span>
          <span className="hc-sig-narrow">{t('homeComposer.signal.failedShort')}</span>
        </>
      );
      break;
  }

  const onPointer = (held: boolean) => (event: PointerEvent) => {
    if (event.pointerType === 'mouse') onHold(held);
  };
  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) onHold(false);
  };

  return (
    // Пока открыты лист, превью, этап 2, меню или клавиатура, над строкой облачко или идёт подсветка, —
    // сигнал прозрачен, клики не ловит и не в порядке Tab; потом возвращается.
    <div className={cn('hc-sig-fade', view.suppressed && 'is-suppressed')} aria-hidden={view.suppressed || undefined}>
      <div
        ref={boxRef}
        className={cn(
          'hc-sig relative flex h-11 items-center gap-2 border border-solid bg-card pl-3 text-[14px] leading-5 text-fg',
          phone ? 'rounded-[14px]' : 'rounded-pill',
          BORDER[view.state],
          view.dismissible && 'has-x',
        )}
        onPointerEnter={onPointer(true)}
        onPointerLeave={onPointer(false)}
        onFocus={() => onHold(true)}
        onBlur={onBlur}
      >
        {view.state === 'generating' && <StatusSheen className="rounded-[inherit]" />}
        <button
          type="button"
          onClick={onShow}
          aria-label={t('homeComposer.signal.aria', { title })}
          className="hc-sig-main relative flex h-full min-w-0 flex-auto items-center gap-2 text-left"
        >
          <AnimatePresence initial={false} mode="popLayout">
            <motion.span
              key={contentKey}
              className="flex min-w-0 items-center gap-2"
              // Смена состояния: текст и значок сменяются перекрёстно (160 мс).
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { duration: reduced ? 0 : 0.16 } }}
              exit={{ opacity: 0, transition: { duration: reduced ? 0 : 0.16 } }}
            >
              <StatusGlyph kind={GLYPH[view.state]} />
              <span className="min-w-0 truncate">
                {label}{' '}
                <span lang={topic ? undefined : 'en'} className="font-serif text-[15px] font-medium">
                  {title}
                </span>
              </span>
              {view.more > 0 && (
                <span className="-ml-1 flex-none whitespace-nowrap text-muted">{t('homeComposer.signal.more', { count: view.more })}</span>
              )}
              {view.cards !== null && (
                <span className="hc-sig-count -ml-1 flex-none whitespace-nowrap text-muted">
                  {t('homeComposer.signal.cards', { count: view.cards })}
                </span>
              )}
              {view.failedOf && (
                <span className="hc-sig-of -ml-1 flex-none whitespace-nowrap text-muted">
                  {t('homeComposer.signal.failedOf', view.failedOf)}
                </span>
              )}
            </motion.span>
          </AnimatePresence>
          {/* Справа — «Показать ↓» (от 480 px) или ↓ в круге 32 px (уже). */}
          <span aria-hidden className="hc-sig-show ml-auto flex-none whitespace-nowrap font-medium text-accent-hi">
            {t('homeComposer.signal.show')} ↓
          </span>
          <span aria-hidden className="hc-sig-arrow ml-auto h-8 w-8 flex-none place-items-center rounded-full bg-elev text-fg">
            <ArrowDownIcon className="h-3.5 w-3.5" />
          </span>
        </button>
        {view.dismissible && (
          // ✕ видимый 32 px, область 44: поля −6 px по бокам, в раскладке занимает 32 px.
          <button
            type="button"
            onClick={onDismiss}
            aria-label={t('homeComposer.signal.dismiss')}
            className="hc-sig-x -mx-1.5 h-11 w-11 flex-none place-items-center text-muted transition-colors"
          >
            <span className="grid h-8 w-8 place-items-center rounded-full">
              <XIcon className="h-3.5 w-3.5" />
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
